import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  getBatchFunnel,
  getBatchStageErrors,
  getFunnelCurationProducts,
  getFunnelCurationRejected,
  getFunnelDeliveryProducts,
  getFunnelProducts,
  getFunnelProductTrace,
  resetFunnelBatch,
} from '../services/batch-funnel.service';
import type {
  BatchFunnel,
  FunnelProductsQuery,
  FunnelUpstreamRef,
} from '../types/batch-funnel.api.types';

/** Funnel poll cadence — the contract asks for 15–30 s while the batch moves. */
export const BATCH_FUNNEL_POLL_MS = 20_000;

/** Page size for every funnel drill-in table. */
export const FUNNEL_PRODUCTS_PAGE_SIZE = 50;

/** True while the verdict says the batch is still moving (poll window). */
export function isFunnelLive(funnel: BatchFunnel | undefined): boolean {
  const state = funnel?.verdict?.state;
  return state === 'RUNNING' || state === 'WAITING';
}

/**
 * Batch Details Funnel for the batch detail page. Polls only while the verdict
 * is RUNNING / WAITING (rule §13.9) and stops once it settles. `enabled` is the
 * `batch_funnel` gate, so a gated-off page never issues the request.
 */
export function useBatchFunnel(jobId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchFunnel(jobId),
    queryFn: () => getBatchFunnel(jobId),
    enabled: enabled && Boolean(jobId),
    staleTime: 0,
    refetchInterval: (query) => (isFunnelLive(query.state.data) ? BATCH_FUNNEL_POLL_MS : false),
  });
}

/**
 * Stage errors for the batch (BE PR #990). Rides the funnel's cadence: polls
 * only while `live` (the funnel verdict is RUNNING / WAITING) and stops once the
 * batch settles. `enabled` is the `batch_funnel` gate.
 */
export function useBatchStageErrors(jobId: string, live: boolean, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchStageErrors(jobId),
    queryFn: () => getBatchStageErrors(jobId),
    enabled: enabled && Boolean(jobId),
    staleTime: 0,
    refetchInterval: live ? BATCH_FUNNEL_POLL_MS : false,
  });
}

/**
 * Products behind a funnel number (zero-based `page`). `query` null keeps the
 * hook idle until a chip / issue / outcome is clicked.
 */
export function useFunnelProducts(jobId: string, query: FunnelProductsQuery | null) {
  const params: FunnelProductsQuery = { size: FUNNEL_PRODUCTS_PAGE_SIZE, ...query };
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchFunnelProducts(jobId, { ...params }),
    queryFn: () => getFunnelProducts(jobId, params),
    enabled: Boolean(jobId) && query !== null,
    placeholderData: keepPreviousData,
  });
}

/** Which upstream listing a `productsPath` points at — decides the table shape. */
export type FunnelUpstreamKind = 'curation-products' | 'curation-rejected' | 'delivery-products';

/** Classifies an upstream ref by service + path; `null` when not renderable. */
export function upstreamKindOf(
  ref: FunnelUpstreamRef | null | undefined,
): FunnelUpstreamKind | null {
  const path = ref?.productsPath;
  if (!path) return null;
  if (ref.service === 'integrations') return 'delivery-products';
  if (ref.service === 'curation') {
    return path.includes('/rejected-items') ? 'curation-rejected' : 'curation-products';
  }
  return null;
}

/**
 * Fetches the upstream listing behind a drill-in (`source: curation | integrations`)
 * through its gateway-absolute `productsPath`. `page` is zero-based here and
 * translated per service (curation is 1-based, integrations uses offset).
 */
export function useFunnelUpstreamProducts(
  jobId: string,
  ref: FunnelUpstreamRef | null | undefined,
  page: number,
) {
  const kind = upstreamKindOf(ref);
  const path = ref?.productsPath ?? '';
  const size = FUNNEL_PRODUCTS_PAGE_SIZE;
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchFunnelUpstream(jobId, path, { page, size }),
    queryFn: async () => {
      switch (kind) {
        case 'curation-products':
          return { kind, data: await getFunnelCurationProducts(path, page + 1, size) } as const;
        case 'curation-rejected':
          return { kind, data: await getFunnelCurationRejected(path, page + 1, size) } as const;
        case 'delivery-products':
          return { kind, data: await getFunnelDeliveryProducts(path, size, page * size) } as const;
        default:
          throw new Error(`Unsupported funnel upstream: ${path}`);
      }
    },
    enabled: Boolean(jobId) && kind !== null,
    placeholderData: keepPreviousData,
  });
}

/** One product's journey; idle until a product row is opened. */
export function useFunnelProductTrace(jobId: string, productId: string | null) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchFunnelTrace(jobId, productId ?? ''),
    queryFn: () => getFunnelProductTrace(jobId, productId ?? ''),
    enabled: Boolean(jobId) && Boolean(productId),
  });
}

/**
 * POSTs `actions.resetPath` and refetches the funnel. The 202 only means the
 * signal was accepted, so the toast says "requested", not "reset".
 */
export function useResetFunnelBatch(jobId: string, messages: { success: string; error: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resetPath: string) => resetFunnelBatch(resetPath),
    onSuccess: () => {
      toast.success(messages.success);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.batchFunnel(jobId),
      });
    },
    onError: (error) => {
      toast.error(extractApiError(error, messages.error));
    },
  });
}
